// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-18) — pins that follow the highest and lowest values
 * (docs/AYNI_XR_FOLLOWING_EXTREMES_PLAN.md).
 *
 * "Mark highest & lowest" used to drop two pins where the extremes sat on
 * the frame it was pressed on. Now they follow: each frame read moves them
 * to that frame's extremes (`PinStore.sample` with a locator), their series
 * becomes the highest (lowest) value frame by frame, and each slot keeps
 * where it was read (`SeriesSlot.at`), which the track draws.
 *
 * Pure: the track's segments from a series, the distance that breaks one,
 * and the budget that keeps the whole-frame read off the playback's back.
 */

import { slotForTime, type MeasurementSeries } from './series'

export interface LatLon {
  readonly lat: number
  readonly lon: number
}

/**
 * A step longer than this (degrees of arc) between two frames is not the
 * extreme moving but another place taking over: the track breaks there
 * rather than drawing a line across the planet.
 */
export const TRACK_BREAK_DEG = 15

/** The angle between two places, in degrees of arc. */
export function arcDeg(a: LatLon, b: LatLon): number {
  const r = Math.PI / 180
  const s = Math.sin(((b.lat - a.lat) * r) / 2) ** 2
    + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(((b.lon - a.lon) * r) / 2) ** 2
  return (2 * Math.asin(Math.min(1, Math.sqrt(s)))) / r
}

/**
 * The track a following pin left, from the window's first frame to
 * `upToMs` (the frame on the globe; null: every frame read). Unread frames
 * are stepped over; a jump past {@link TRACK_BREAK_DEG} starts a new
 * segment. Within a segment each longitude is carried within 180° of the
 * one before, so a line across the date line stays short (MapLibre and the
 * headset both draw a longitude past ±180 where it belongs). Segments of a
 * single point are left out: nothing to draw.
 */
export function trackSegments(series: MeasurementSeries | null, upToMs: number | null): LatLon[][] {
  if (!series) return []
  let end = series.slots.length - 1
  if (upToMs !== null) {
    const slot = slotForTime(series, upToMs)
    // Outside the window: after it, all of it; before it, none.
    end = slot >= 0 ? slot : upToMs > series.endMs ? end : -1
  }
  const out: LatLon[][] = []
  let seg: LatLon[] = []
  let prev: LatLon | null = null
  for (let i = 0; i <= end; i++) {
    const at = series.slots[i]?.at
    if (!series.slots[i]?.sampled || !at) continue
    if (prev && arcDeg(prev, at) > TRACK_BREAK_DEG) {
      if (seg.length > 1) out.push(seg)
      seg = []
      prev = null
    }
    let lon = at.lon
    if (prev) {
      while (lon - prev.lon > 180) lon -= 360
      while (lon - prev.lon < -180) lon += 360
    }
    const pt = { lat: at.lat, lon }
    seg.push(pt)
    prev = pt
  }
  if (seg.length > 1) out.push(seg)
  return out
}

/**
 * A track's segments with points added along the great circle, at most
 * `stepDeg` apart, for a map that draws straight in longitude and latitude
 * (MapLibre): near a pole a straight step between two frames there bends
 * far off the path the pin took. Longitudes stay continuous, as
 * {@link trackSegments} leaves them.
 */
export function densifyTrack(segments: readonly (readonly LatLon[])[], stepDeg = 2): LatLon[][] {
  const r = Math.PI / 180
  const unit = (p: LatLon) => [Math.cos(p.lat * r) * Math.cos(p.lon * r), Math.cos(p.lat * r) * Math.sin(p.lon * r), Math.sin(p.lat * r)]
  return segments.map((seg) => {
    const out: LatLon[] = seg.length ? [{ lat: seg[0].lat, lon: seg[0].lon }] : []
    for (let i = 1; i < seg.length; i++) {
      const a = seg[i - 1]
      const b = seg[i]
      const n = Math.max(1, Math.ceil(arcDeg(a, b) / stepDeg - 1e-9))
      const ua = unit(a)
      const ub = unit(b)
      for (let k = 1; k < n; k++) {
        const f = k / n
        const x = ua[0] + (ub[0] - ua[0]) * f
        const y = ua[1] + (ub[1] - ua[1]) * f
        const z = ua[2] + (ub[2] - ua[2]) * f
        let lon = Math.atan2(y, x) / r
        const prev = out[out.length - 1].lon
        while (lon - prev > 180) lon -= 360
        while (lon - prev < -180) lon += 360
        out.push({ lat: Math.atan2(z, Math.hypot(x, y)) / r, lon })
      }
      out.push({ lat: b.lat, lon: b.lon })
    }
    return out
  })
}

/**
 * Finding an extreme reads the whole frame back from the graphics card,
 * which costs a few milliseconds on a desktop and more on a headset or a
 * phone. The budget lets it run only while the time it takes stays under
 * `share` of the wall clock: after a read that took `t` ms, the next waits
 * `t × (1 / share − 1)`. A frame skipped is left unread for the following
 * pins (their slot fills on a later lap), never read at a stale place.
 */
export interface LocateBudget {
  /** `fn`'s result, or null when it is too soon to run it again. */
  run<T>(fn: () => T): T | null
  /** Forget the last cost: the next call runs (a press, a new dataset). */
  reset(): void
}

export function createLocateBudget(share = 0.1, now: () => number = () => performance.now()): LocateBudget {
  let nextAt = -Infinity
  return {
    run(fn) {
      const start = now()
      if (start < nextAt) return null
      const result = fn()
      const cost = Math.max(0, now() - start)
      nextAt = start + cost / share
      return result
    },
    reset() {
      nextAt = -Infinity
    },
  }
}
